/**
 * SmartTaskChat — Assistant IA central du dashboard
 *
 * Design : glassmorphism + glow violet, élément héros du dashboard
 * Intelligence : Groq LLaMA avec contexte riche (heure, météo, profil, tâches existantes)
 * UX : placeholder animé, suggestions contextuelles, preview de tâche, multi-tour
 */
import React, { memo, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, Sparkles, X, Check, Loader2, Clock, ListChecks,
  Wand2, ArrowRight, RotateCcw,
} from 'lucide-react';
import { haptic } from '../../shared/utils/haptics';

// ─── Config ─────────────────────────────────────────────────────────────────
const GROQ_URL = 'https://api.groq.com/openai/v1/chat/completions';
const MODEL = 'llama-3.3-70b-versatile';
const CATEGORIES = ['vital', 'chore', 'pleasure', 'reward'];

const CAT = {
  vital:    { label: 'Vital',      emoji: '🔴', text: '#f87171', bg: 'rgba(239,68,68,0.10)', border: 'rgba(239,68,68,0.22)', glow: 'rgba(239,68,68,0.08)' },
  chore:    { label: 'Corvée',     emoji: '🟠', text: '#fbbf24', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.22)', glow: 'rgba(245,158,11,0.08)' },
  pleasure: { label: 'Plaisir',    emoji: '🟢', text: '#34d399', bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.22)', glow: 'rgba(16,185,129,0.08)' },
  reward:   { label: 'Récompense', emoji: '🟣', text: '#a78bfa', bg: 'rgba(139,92,246,0.10)', border: 'rgba(139,92,246,0.22)', glow: 'rgba(139,92,246,0.08)' },
};

// ─── Contextual placeholders ────────────────────────────────────────────────
function getContextualHints() {
  const h = new Date().getHours();
  if (h < 8)  return ['Prendre mes médicaments', 'Routine du matin', 'Petit-déjeuner'];
  if (h < 12) return ['Session de travail', 'Courses rapides', 'Sport ce matin'];
  if (h < 14) return ['Pause déjeuner', 'Marche digestive', 'Appel important'];
  if (h < 18) return ['Rendez-vous à planifier', 'Ménage rapide', 'Projet perso'];
  if (h < 21) return ['Cuisiner le dîner', 'Sortie cinéma', 'Sport en soirée'];
  return ['Préparer demain', 'Routine du soir', 'Méditation'];
}

// ─── Load user profile from localStorage ────────────────────────────────────
function getUserContext() {
  try {
    const raw = localStorage.getItem('adhd-user-profile');
    if (!raw) return '';
    const p = JSON.parse(raw);
    const parts = [];
    if (p.firstName) parts.push(`Prénom: ${p.firstName}`);
    if (p.city) parts.push(`Ville: ${p.city}`);
    if (p.hobbies?.length) parts.push(`Hobbies: ${p.hobbies.join(', ')}`);
    if (p.nearbyPlaces?.length) parts.push(`Lieux proches: ${p.nearbyPlaces.map(l => l.name).join(', ')}`);
    if (p.healthInfo?.hasMedication) parts.push(`Médicaments: ${p.healthInfo.medicationName || 'oui'} (${(p.healthInfo.medicationTimes||[]).join(', ')})`);
    if (p.workSchedule?.type) parts.push(`Travail: ${p.workSchedule.type} ${p.workSchedule.startTime||''}-${p.workSchedule.endTime||''}`);
    if (p.preferences?.wakeTime) parts.push(`Réveil: ${p.preferences.wakeTime}`);
    return parts.length ? `\nPROFIL UTILISATEUR:\n${parts.join('\n')}` : '';
  } catch { return ''; }
}

// ─── Build system prompt ────────────────────────────────────────────────────
function buildSystemPrompt(existingTaskNames, weather) {
  const now = new Date();
  const dateStr = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const profile = getUserContext();
  const existingCtx = existingTaskNames.length
    ? `\nTÂCHES DÉJÀ PRÉVUES AUJOURD'HUI:\n${existingTaskNames.map(t => `- ${t}`).join('\n')}`
    : '\nAucune tâche prévue aujourd\'hui.';
  const weatherCtx = weather ? `\nMÉTÉO: ${weather.description || ''} ${weather.temp ? weather.temp+'°C' : ''}` : '';

  return `Tu es un assistant ADHD intelligent, bienveillant et concis. Tu parles français.
Tu DEVINES ce que l'utilisateur veut faire à partir d'indices minimes.

CONTEXTE:
Date: ${dateStr}
Heure: ${timeStr}${weatherCtx}${profile}${existingCtx}

RÈGLES:
1. Réponds TOUJOURS en JSON valide UNIQUEMENT (pas de texte, pas de markdown)
2. Déduis un MAXIMUM d'informations toi-même:
   - Si l'utilisateur dit juste "sport" → crée une tâche complète de sport avec l'heure la plus logique
   - Si l'utilisateur dit "courses" → déduis supermarché, durée ~45min, catégorie chore
   - Si vague → devine l'intention la plus probable et propose
3. Génère TOUJOURS 3-5 sous-tâches ULTRA-CONCRÈTES et actionnables
4. NE DEMANDE des précisions QUE si le message est vraiment incompréhensible
5. Utilise le profil pour personnaliser (lieux proches, hobbies, médicaments)

STRUCTURE JSON:

Si tu crées la tâche:
{
  "status": "complete",
  "task": {
    "task": "Nom court et clair",
    "time": "HH:MM",
    "duration": minutes,
    "category": "vital|chore|pleasure|reward",
    "desc": "1 ligne de contexte",
    "instructions": ["Étape 1 concrète", "Étape 2", "Étape 3", "Étape 4"]
  },
  "message": "Confirmation courte et motivante (1 ligne max)"
}

Si vraiment incompréhensible:
{
  "status": "need_info",
  "message": "Question ultra-courte",
  "suggestions": ["Suggestion 1", "Suggestion 2", "Suggestion 3"]
}

CATÉGORIES: vital=médical/hygiène, chore=travail/ménage/admin, pleasure=sport/loisirs/sorties, reward=récompenses/détente
DÉDUCTION DURÉE: sport=60, courses=45, ménage=30, cuisine=45, travail=120, médecin=30, marche=30, lecture=45`;
}

// ─── Groq call ──────────────────────────────────────────────────────────────
async function callGroq(messages, existingTaskNames, weather) {
  const apiKey = process.env.REACT_APP_GROQ_API_KEY;
  if (!apiKey) throw new Error('Clé API Groq manquante');

  const res = await fetch(GROQ_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
    body: JSON.stringify({
      model: MODEL,
      messages: [
        { role: 'system', content: buildSystemPrompt(existingTaskNames, weather) },
        ...messages,
      ],
      temperature: 0.35,
      max_tokens: 1024,
    }),
  });

  if (!res.ok) throw new Error(`Groq ${res.status}`);
  const data = await res.json();
  const text = data.choices?.[0]?.message?.content || '';
  const clean = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
  const match = clean.match(/\{[\s\S]*\}/);
  if (!match) throw new Error('Non-JSON');
  return JSON.parse(match[0]);
}

// ─── Local fallback parser (when Groq unavailable) ─────────────────────
function localParseFallback(text) {
  if (!text || text.trim().length < 2) return null;

  // Extract time (HH:MM or Hh)
  const timeMatch = text.match(/(\d{1,2})[h:](\d{2})?/);
  const time = timeMatch ? `${String(timeMatch[1]).padStart(2,'0')}:${timeMatch[2] || '00'}` : null;

  // Extract duration
  const durMatch = text.match(/(\d+)\s*min/i);
  const duration = durMatch ? parseInt(durMatch[1]) : 30;

  // Infer category
  const lower = text.toLowerCase();
  let category = 'chore';
  if (/medicament|pilule|docteur|medecin|rdv|sante|dent/i.test(lower)) category = 'vital';
  else if (/sport|marche|film|jeu|ami|resto|pause|cafe|lecture|yoga/i.test(lower)) category = 'pleasure';
  else if (/cadeau|achat|shopping|recompense/i.test(lower)) category = 'reward';

  // Clean task name (remove time/duration parts)
  let task = text.replace(/[aà]\s*\d{1,2}[h:]\d{0,2}/i, '')
    .replace(/pendant\s*\d+\s*min/i, '')
    .replace(/\d+\s*min/i, '')
    .trim();
  if (!task) task = text.trim();

  return { task, time, duration, category, desc: '', instructions: [] };
}

// ─── Typing indicator ───────────────────────────────────────────────────────
const TypingDots = memo(function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-3 py-2.5">
      <Wand2 className="w-3 h-3 mr-1" style={{ color: '#a78bfa' }} />
      {[0, 1, 2].map(i => (
        <motion.div
          key={i}
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: '#a78bfa' }}
          animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.1, 0.8] }}
          transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
        />
      ))}
    </div>
  );
});

// ─── Task preview card ──────────────────────────────────────────────────────
const TaskPreview = memo(function TaskPreview({ task, message, onConfirm }) {
  const cat = CAT[task?.category] || CAT.chore;

  // Smart links: contextual suggestions for this task
  const [smartSuggestion, setSmartSuggestion] = useState(null);
  useEffect(() => {
    if (!task) return;
    import('../../services/smartLinksService').then(mod => {
      const suggestion = mod.default.getMainSuggestionForTask(task);
      setSmartSuggestion(suggestion);
    }).catch(() => {});
  }, [task]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      className="rounded-2xl overflow-hidden"
      style={{
        background: `linear-gradient(160deg, ${cat.bg} 0%, rgba(14,14,20,0.98) 100%)`,
        border: `1px solid ${cat.border}`,
        boxShadow: `0 0 28px ${cat.glow}`,
      }}
    >
      {/* Header */}
      <div className="px-3.5 pt-3 pb-2">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ color: cat.text, background: `${cat.text}15`, border: `1px solid ${cat.text}22` }}>
            {cat.emoji} {cat.label}
          </span>
          <div className="flex items-center gap-1.5 ml-auto" style={{ color: 'rgba(255,255,255,0.30)' }}>
            <Clock className="w-3 h-3" />
            <span className="text-[10px] font-mono">{task.time}</span>
            <span className="text-[10px]">· {task.duration}min</span>
          </div>
        </div>

        <h3 className="text-[15px] font-bold leading-tight mb-1"
          style={{ color: 'rgba(255,255,255,0.90)' }}>
          {task.task}
        </h3>
        {task.desc && (
          <p className="text-[10px] mb-2" style={{ color: 'rgba(255,255,255,0.30)' }}>{task.desc}</p>
        )}
      </div>

      {/* Subtasks */}
      {task.instructions?.length > 0 && (
        <div className="px-3.5 pb-2">
          <div className="flex items-center gap-1.5 mb-1.5">
            <ListChecks className="w-3 h-3" style={{ color: 'rgba(255,255,255,0.25)' }} />
            <span className="text-[9px] font-semibold uppercase tracking-wider"
              style={{ color: 'rgba(255,255,255,0.25)' }}>
              {task.instructions.length} étapes
            </span>
          </div>
          <div className="space-y-1">
            {task.instructions.map((step, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>
                  <span className="text-[8px] font-bold" style={{ color: 'rgba(255,255,255,0.30)' }}>{i + 1}</span>
                </div>
                <span className="text-[11px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  {step}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Smart Links — contextual suggestions */}
      {smartSuggestion && (
        <div className="px-3.5 pb-2">
          <div className="rounded-lg overflow-hidden"
            style={{ background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.12)' }}>
            <div className="px-2.5 py-1.5">
              <span className="text-[9px] font-semibold uppercase tracking-wider"
                style={{ color: 'rgba(139,92,246,0.6)' }}>
                {smartSuggestion.title}
              </span>
            </div>
            <div className="px-2.5 pb-2 space-y-1">
              {(smartSuggestion.items || []).slice(0, 3).map((item, i) => (
                <a
                  key={i}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 py-1 text-[10px] hover:opacity-80 transition-opacity"
                  style={{ color: 'rgba(167,139,250,0.7)' }}
                >
                  <ArrowRight className="w-2.5 h-2.5 shrink-0" />
                  <span className="truncate">{item.name}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-3.5 pb-3 pt-2 flex items-center gap-2"
        style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        {message && (
          <span className="text-[10px] flex-1" style={{ color: 'rgba(255,255,255,0.35)' }}>
            {message}
          </span>
        )}
        <motion.button
          onClick={onConfirm}
          className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-[11px] font-bold ml-auto"
          style={{
            background: `linear-gradient(135deg, ${cat.text}22, ${cat.text}11)`,
            border: `1px solid ${cat.text}33`,
            color: cat.text,
          }}
          whileTap={{ scale: 0.92 }}
          whileHover={{ scale: 1.02 }}
        >
          <Check className="w-3.5 h-3.5" />
          Ajouter
        </motion.button>
      </div>
    </motion.div>
  );
});

// ─── AI suggestion pill ─────────────────────────────────────────────────────
const SuggestionPill = memo(function SuggestionPill({ label, onClick }) {
  return (
    <motion.button
      onClick={onClick}
      className="px-3 py-1.5 rounded-xl text-[11px] whitespace-nowrap transition-all"
      style={{
        background: 'rgba(139,92,246,0.06)',
        border: '1px solid rgba(139,92,246,0.14)',
        color: 'rgba(255,255,255,0.50)',
      }}
      whileTap={{ scale: 0.93 }}
      whileHover={{ background: 'rgba(139,92,246,0.12)', color: 'rgba(255,255,255,0.70)' }}
    >
      {label}
    </motion.button>
  );
});

// ─── Chat bubble ────────────────────────────────────────────────────────────
const Bubble = memo(function Bubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <motion.div
      initial={{ opacity: 0, y: 6, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.2 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`max-w-[88%] rounded-2xl px-3.5 py-2 ${isUser ? 'rounded-br-md' : 'rounded-bl-md'}`}
        style={isUser
          ? { background: 'rgba(99,102,241,0.16)', border: '1px solid rgba(99,102,241,0.22)' }
          : { background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.06)' }
        }
      >
        <p className="text-[12px] leading-relaxed" style={{ color: isUser ? 'rgba(199,210,254,0.90)' : 'rgba(255,255,255,0.72)' }}>
          {!isUser && <Sparkles className="w-3 h-3 inline-block mr-1.5 -mt-0.5" style={{ color: '#a78bfa' }} />}
          {msg.content}
        </p>
      </div>
    </motion.div>
  );
});

// ─── Animated placeholder ───────────────────────────────────────────────────
function useRotatingPlaceholder() {
  const hints = useMemo(() => getContextualHints(), []);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx(i => (i + 1) % hints.length), 3500);
    return () => clearInterval(id);
  }, [hints.length]);
  return hints[idx];
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export const SmartTaskChat = memo(function SmartTaskChat({ onTaskCreated, existingTasks, weather }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [groqHistory, setGroqHistory] = useState([]);
  const [pendingTask, setPendingTask] = useState(null); // task preview before confirm
  const [pendingMessage, setPendingMessage] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const placeholder = useRotatingPlaceholder();

  // Existing task names for context
  const existingNames = useMemo(() => {
    const today = new Date();
    const ds = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
    return (existingTasks || []).filter(t => t.date === ds).map(t => t.task);
  }, [existingTasks]);

  // Contextual quick suggestions
  const quickSuggestions = useMemo(() => getContextualHints(), []);

  // Scroll to bottom
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pendingTask, isLoading]);

  // Focus on open
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 250);
  }, [isOpen]);

  // ─── Send message ─────────────────────────────────────────────────────
  const send = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || isLoading) return;
    haptic.tap();
    setInput('');
    setPendingTask(null);
    setAiSuggestions([]);

    const userMsg = { role: 'user', content: msg };
    setMessages(prev => [...prev, userMsg]);
    const newHistory = [...groqHistory, { role: 'user', content: msg }];
    setGroqHistory(newHistory);

    setIsLoading(true);
    try {
      const result = await callGroq(newHistory, existingNames, weather?.weather);

      if (result.status === 'complete' && result.task) {
        // Show task preview
        setPendingTask(result.task);
        setPendingMessage(result.message || '');
        setGroqHistory(prev => [...prev, { role: 'assistant', content: JSON.stringify(result) }]);
      } else if (result.status === 'need_info') {
        setMessages(prev => [...prev, { role: 'assistant', content: result.message }]);
        setGroqHistory(prev => [...prev, { role: 'assistant', content: JSON.stringify(result) }]);
        if (result.suggestions?.length) setAiSuggestions(result.suggestions);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: result.message || 'Reformule ?' }]);
      }
    } catch (err) {
      console.warn('SmartTaskChat Groq fallback:', err.message);
      // Fallback: parse locally using simple heuristics
      try {
        const fallbackTask = localParseFallback(msg);
        if (fallbackTask) {
          setPendingTask(fallbackTask);
          setPendingMessage('(Mode hors-ligne) Voici ce que j\'ai compris :');
        } else {
          setMessages(prev => [...prev, { role: 'assistant', content: 'IA indisponible. Essaie un format simple : "Courses a 14h pendant 30min"' }]);
        }
      } catch {
        setMessages(prev => [...prev, { role: 'assistant', content: 'IA indisponible. Essaie un format simple : "Tache a HH:MM"' }]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, groqHistory, existingNames, weather]);

  // ─── Confirm task creation ────────────────────────────────────────────
  const confirmTask = useCallback(() => {
    if (!pendingTask) return;
    haptic.medium();

    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

    onTaskCreated?.({
      task: pendingTask.task,
      time: pendingTask.time || null,
      duration: pendingTask.duration || 30,
      category: CATEGORIES.includes(pendingTask.category) ? pendingTask.category : 'chore',
      desc: pendingTask.desc || '',
      instructions: (pendingTask.instructions || []).filter(Boolean),
      date: dateStr,
      iconName: 'Sparkles',
      isAIGenerated: true,
    });

    // Reset
    setTimeout(() => {
      setPendingTask(null);
      setPendingMessage('');
      setMessages([]);
      setGroqHistory([]);
      setAiSuggestions([]);
      setIsOpen(false);
    }, 600);
  }, [pendingTask, onTaskCreated]);

  // ─── Reset conversation ───────────────────────────────────────────────
  const reset = useCallback(() => {
    setMessages([]);
    setGroqHistory([]);
    setPendingTask(null);
    setPendingMessage('');
    setAiSuggestions([]);
    setInput('');
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  }, [send]);

  // ═══════════════════════════════════════════════════════════════════════
  // COLLAPSED — Prominent input bar
  // ═══════════════════════════════════════════════════════════════════════
  if (!isOpen) {
    return (
      <div className="px-4 py-1">
        <motion.button
          onClick={() => { haptic.tap(); setIsOpen(true); }}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(139,92,246,0.08) 0%, rgba(99,102,241,0.05) 100%)',
            border: '1px solid rgba(139,92,246,0.16)',
            boxShadow: '0 0 24px rgba(139,92,246,0.04), inset 0 1px 0 rgba(255,255,255,0.03)',
          }}
          whileTap={{ scale: 0.985 }}
        >
          {/* Glow */}
          <div className="absolute -top-6 -left-6 w-20 h-20 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)' }} />

          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(139,92,246,0.14)', border: '1px solid rgba(139,92,246,0.22)' }}>
            <Sparkles className="w-4 h-4" style={{ color: '#a78bfa' }} />
          </div>

          <div className="flex-1 text-left">
            <AnimatePresence mode="wait">
              <motion.span
                key={placeholder}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.25 }}
                className="text-[13px] block"
                style={{ color: 'rgba(255,255,255,0.30)' }}
              >
                {placeholder}
              </motion.span>
            </AnimatePresence>
            <span className="text-[9px]" style={{ color: 'rgba(255,255,255,0.12)' }}>
              Tape pour créer avec l'IA
            </span>
          </div>

          <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(139,92,246,0.35)' }} />
        </motion.button>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // EXPANDED — Full chat interface
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      className="mx-4 my-1 rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, rgba(18,14,30,0.98) 0%, rgba(10,10,16,0.99) 100%)',
        border: '1px solid rgba(139,92,246,0.18)',
        boxShadow: '0 4px 40px rgba(139,92,246,0.08), 0 0 0 1px rgba(0,0,0,0.3)',
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-2.5"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(139,92,246,0.15)' }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: '#a78bfa' }} />
          </div>
          <span className="text-[12px] font-semibold" style={{ color: 'rgba(255,255,255,0.60)' }}>
            Assistant IA
          </span>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <motion.button onClick={reset} whileTap={{ scale: 0.85 }}
              className="p-1.5 rounded-lg" style={{ color: 'rgba(255,255,255,0.20)' }}>
              <RotateCcw className="w-3 h-3" />
            </motion.button>
          )}
          <motion.button onClick={() => { reset(); setIsOpen(false); }} whileTap={{ scale: 0.85 }}
            className="p-1.5 rounded-lg" style={{ color: 'rgba(255,255,255,0.20)' }}>
            <X className="w-3.5 h-3.5" />
          </motion.button>
        </div>
      </div>

      {/* ── Messages area ───────────────────────────────────────────── */}
      <div ref={scrollRef} className="max-h-[280px] overflow-y-auto px-3 py-3 space-y-2.5">
        {/* Empty state with suggestions */}
        {messages.length === 0 && !pendingTask && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-3"
          >
            <p className="text-[11px] mb-3" style={{ color: 'rgba(255,255,255,0.20)' }}>
              Dis-moi ce que tu veux faire
            </p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {quickSuggestions.map((s, i) => (
                <SuggestionPill key={i} label={s} onClick={() => send(s)} />
              ))}
            </div>
          </motion.div>
        )}

        {/* Chat messages */}
        {messages.map((msg, i) => (
          <Bubble key={i} msg={msg} />
        ))}

        {/* Loading */}
        {isLoading && <TypingDots />}

        {/* AI follow-up suggestions */}
        <AnimatePresence>
          {aiSuggestions.length > 0 && !isLoading && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="flex flex-wrap gap-1.5 pt-1"
            >
              {aiSuggestions.map((s, i) => (
                <SuggestionPill key={i} label={s} onClick={() => send(s)} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Task preview */}
        <AnimatePresence>
          {pendingTask && (
            <TaskPreview
              key="preview"
              task={pendingTask}
              message={pendingMessage}
              onConfirm={confirmTask}
            />
          )}
        </AnimatePresence>
      </div>

      {/* ── Input bar ───────────────────────────────────────────────── */}
      {!pendingTask && (
        <div className="flex items-center gap-2 px-3 pb-3 pt-1">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 text-[13px] px-4 py-2.5 rounded-xl outline-none transition-all"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${input.trim() ? 'rgba(139,92,246,0.25)' : 'rgba(255,255,255,0.07)'}`,
              color: 'rgba(255,255,255,0.85)',
              boxShadow: input.trim() ? '0 0 12px rgba(139,92,246,0.06)' : 'none',
            }}
            disabled={isLoading}
          />
          <motion.button
            onClick={() => send()}
            disabled={!input.trim() || isLoading}
            className="p-2.5 rounded-xl transition-all"
            style={{
              background: input.trim()
                ? 'linear-gradient(135deg, rgba(139,92,246,0.28), rgba(99,102,241,0.22))'
                : 'rgba(255,255,255,0.03)',
              border: `1px solid ${input.trim() ? 'rgba(139,92,246,0.35)' : 'rgba(255,255,255,0.06)'}`,
              color: input.trim() ? '#c4b5fd' : 'rgba(255,255,255,0.12)',
              boxShadow: input.trim() ? '0 0 16px rgba(139,92,246,0.10)' : 'none',
            }}
            whileTap={{ scale: 0.88 }}
          >
            <Send className="w-4 h-4" />
          </motion.button>
        </div>
      )}
    </motion.div>
  );
});

export default SmartTaskChat;
